// ── Role → color map for preview blocks ──────────────────────────────────────
export const ROLE_STYLE: Record<string, { bg: string; border: string; text: string }> = {
    kpi:     { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
    bar:     { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
    line:    { bg: '#dcfce7', border: '#86efac', text: '#14532d' },
    area:    { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
    donut:   { bg: '#fef3c7', border: '#fcd34d', text: '#78350f' },
    pie:     { bg: '#ffedd5', border: '#fdba74', text: '#7c2d12' },
    table:   { bg: '#ede9fe', border: '#c4b5fd', text: '#4c1d95' },
    scatter: { bg: '#fdf4ff', border: '#e879f9', text: '#701a75' },
    gauge:   { bg: '#fff1f2', border: '#fda4af', text: '#881337' },
    filter:  { bg: '#f0fdf4', border: '#86efac', text: '#166534' },
    hbar:    { bg: '#ecfdf5', border: '#6ee7b7', text: '#065f46' },
};

// ══════════════════════════════════════════════════════════════════════════════
//  DASHBOARD TEMPLATES  (GRID_W = 120 cols, GRID_H varies per template)
// ══════════════════════════════════════════════════════════════════════════════
export const DASHBOARD_TEMPLATES = [

    // ── 1 ─ Executive KPI Overview ───────────────────────────────────────────
    {
        id: 'executive-kpi',
        name: 'Executive KPI Overview',
        description: '4 KPI scorecards across the top with a monthly trend bar and growth line chart below — ideal for C-suite views.',
        icon: '📊', category: 'Leadership', tags: ['KPI','Bar','Line'],
        gridH: 68,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',  label:'Revenue',      x:0,  y:12,  w:30, h:18 },
            { role:'kpi',  label:'Customers',    x:30, y:12,  w:30, h:18 },
            { role:'kpi',  label:'Avg Order',    x:60, y:12,  w:30, h:18 },
            { role:'kpi',  label:'Growth %',     x:90, y:12,  w:30, h:18 },
            { role:'bar',  label:'Monthly Rev',  x:0,  y:30, w:60, h:38 },
            { role:'line', label:'Growth Trend', x:60, y:30, w:60, h:38 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',  title:'Total Revenue',   layout:{x:0,  y:12,  w:30, h:18} },
            { type:'kpi',  title:'Total Customers', layout:{x:30, y:12,  w:30, h:18} },
            { type:'kpi',  title:'Avg Order Value', layout:{x:60, y:12,  w:30, h:18} },
            { type:'kpi',  title:'Growth Rate',     layout:{x:90, y:12,  w:30, h:18} },
            { type:'bar',  title:'Monthly Revenue', layout:{x:0,  y:30, w:60, h:38} },
            { type:'line', title:'Trend Over Time', layout:{x:60, y:30, w:60, h:38} },
        ],
    },

    // ── 2 ─ Sales Analytics Board ─────────────────────────────────────────────
    {
        id: 'sales-analytics',
        name: 'Sales Analytics Board',
        description: '3 KPIs, ranked bar, category donut, area trend, and a full-width transaction table at the bottom.',
        icon: '📈', category: 'Sales', tags: ['KPI','Donut','Table'],
        gridH: 98,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Sales',        x:0,  y:12,  w:40, h:18 },
            { role:'kpi',   label:'Units',        x:40, y:12,  w:40, h:18 },
            { role:'kpi',   label:'Conv Rate',    x:80, y:12,  w:40, h:18 },
            { role:'bar',   label:'Top Products', x:0,  y:30, w:45, h:38 },
            { role:'donut', label:'By Category',  x:45, y:30, w:33, h:38 },
            { role:'area',  label:'Sales Trend',  x:78, y:30, w:42, h:38 },
            { role:'table', label:'Transactions', x:0,  y:68, w:120,h:30 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'Total Sales',         layout:{x:0,  y:12,  w:40, h:18} },
            { type:'kpi',   title:'Units Sold',          layout:{x:40, y:12,  w:40, h:18} },
            { type:'kpi',   title:'Conversion Rate',     layout:{x:80, y:12,  w:40, h:18} },
            { type:'bar',   title:'Top Products',        layout:{x:0,  y:30, w:45, h:38} },
            { type:'donut', title:'Sales by Category',   layout:{x:45, y:30, w:33, h:38} },
            { type:'area',  title:'Sales Over Time',     layout:{x:78, y:30, w:42, h:38} },
            { type:'table', title:'Transaction Details', layout:{x:0,  y:68, w:120,h:30} },
        ],
    },

    // ── 3 ─ Billing Anomaly Monitor ───────────────────────────────────────────
    {
        id: 'billing-anomaly',
        name: 'Billing Anomaly Monitor',
        description: '4 anomaly KPIs, dual deviation line + bar charts, a distribution donut, and an anomaly detail table.',
        icon: '🔍', category: 'Healthcare', tags: ['Anomaly','Donut','Table'],
        gridH: 104,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Records',      x:0,  y:12,  w:30, h:18 },
            { role:'kpi',   label:'Anomalies',    x:30, y:12,  w:30, h:18 },
            { role:'kpi',   label:'At Risk',      x:60, y:12,  w:30, h:18 },
            { role:'kpi',   label:'Undercharge',  x:90, y:12,  w:30, h:18 },
            { role:'line',  label:'Diag Codes',   x:0,  y:30, w:60, h:38 },
            { role:'bar',   label:'Anomaly Bar',  x:60, y:30, w:60, h:38 },
            { role:'donut', label:'Distribution', x:0,  y:68, w:42, h:36 },
            { role:'table', label:'Anomaly Data', x:42, y:68, w:78, h:36 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'Total Records',              layout:{x:0,  y:12,  w:30, h:18} },
            { type:'kpi',   title:'Total Anomalies Detected',   layout:{x:30, y:12,  w:30, h:18} },
            { type:'kpi',   title:'Charges at Risk',            layout:{x:60, y:12,  w:30, h:18} },
            { type:'kpi',   title:'Undercharge',                layout:{x:90, y:12,  w:30, h:18} },
            { type:'line',  title:'Diagnosis Code Deviations',  layout:{x:0,  y:30, w:60, h:38} },
            { type:'bar',   title:'Anomaly Bar Chart',          layout:{x:60, y:30, w:60, h:38} },
            { type:'donut', title:'Billing Distribution',       layout:{x:0,  y:68, w:42, h:36} },
            { type:'table', title:'Anomaly Detail Data',        layout:{x:42, y:68, w:78, h:36} },
        ],
    },

    // ── 4 ─ Appointment Summary ───────────────────────────────────────────────
    {
        id: 'appointment-summary',
        name: 'Appointment Summary',
        description: '5 appointment KPIs, 2 patient-type donuts, appointment status table, and dual area charts for growth & fill rate.',
        icon: '🏥', category: 'Healthcare', tags: ['KPI','Donut','Area'],
        gridH: 102,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Appts',         x:0,  y:12,  w:24, h:18 },
            { role:'kpi',   label:'Cancels',       x:24, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Cancel %',      x:48, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Slot Fill',     x:72, y:12,  w:24, h:18 },
            { role:'kpi',   label:'No-Show',       x:96, y:12,  w:24, h:18 },
            { role:'donut', label:'New vs Recur',  x:0,  y:30, w:28, h:36 },
            { role:'donut', label:'Walk-in/Sched', x:28, y:30, w:28, h:36 },
            { role:'table', label:'Encounters',    x:56, y:30, w:64, h:36 },
            { role:'area',  label:'Growth Trend',  x:0,  y:66, w:60, h:36 },
            { role:'area',  label:'Fill Rate',     x:60, y:66, w:60, h:36 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'No. of Appointments',        layout:{x:0,  y:12,  w:24, h:18} },
            { type:'kpi',   title:'Cancellation Count',         layout:{x:24, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Cancellation Rate %',        layout:{x:48, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Slot Fill Rate %',           layout:{x:72, y:12,  w:24, h:18} },
            { type:'kpi',   title:'No Show Rate %',             layout:{x:96, y:12,  w:24, h:18} },
            { type:'donut', title:'New vs Recurring Patients',  layout:{x:0,  y:30, w:28, h:36} },
            { type:'donut', title:'Walk-ins vs Scheduled',      layout:{x:28, y:30, w:28, h:36} },
            { type:'table', title:'Encounters by Appt Status',  layout:{x:56, y:30, w:64, h:36} },
            { type:'area',  title:'Growth Trend',               layout:{x:0,  y:66, w:60, h:36} },
            { type:'area',  title:'Fill Rate',                  layout:{x:60, y:66, w:60, h:36} },
        ],
    },

    // ── 5 ─ Charges Summary ───────────────────────────────────────────────────
    {
        id: 'charges-summary',
        name: 'Charges Summary',
        description: '5 charge KPIs, service-line horizontal bar, providers bar, top insurance bar, and CPT code ranking table.',
        icon: '💰', category: 'Finance', tags: ['Charges','Bar','Table'],
        gridH: 104,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Charges',       x:0,  y:12,  w:24, h:18 },
            { role:'kpi',   label:'Allowed',       x:24, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Adjustment',    x:48, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Owed',          x:72, y:12,  w:24, h:18 },
            { role:'kpi',   label:'Charge Lag',    x:96, y:12,  w:24, h:18 },
            { role:'bar',   label:'Service Line',  x:0,  y:30, w:60, h:38 },
            { role:'bar',   label:'Top Providers', x:60, y:30, w:60, h:38 },
            { role:'bar',   label:'Top Insurance', x:0,  y:68, w:60, h:36 },
            { role:'table', label:'Top CPTs',      x:60, y:68, w:60, h:36 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'Total Charges',          layout:{x:0,  y:12,  w:24, h:18} },
            { type:'kpi',   title:'Allowed Amount',         layout:{x:24, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Adjustment Amount',      layout:{x:48, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Total Owed Amount',      layout:{x:72, y:12,  w:24, h:18} },
            { type:'kpi',   title:'Charge Lag',             layout:{x:96, y:12,  w:24, h:18} },
            { type:'bar',   title:'Service Line by Charges',layout:{x:0,  y:30, w:60, h:38} },
            { type:'bar',   title:'Top Providers by Charges',layout:{x:60,y:30, w:60, h:38} },
            { type:'bar',   title:'Top 10 Insurance',       layout:{x:0,  y:68, w:60, h:36} },
            { type:'table', title:'Top 10 CPT Codes',       layout:{x:60, y:68, w:60, h:36} },
        ],
    },

    // ── 6 ─ Payment Analytics ─────────────────────────────────────────────────
    {
        id: 'payment-analytics',
        name: 'Payment Analytics',
        description: '6 payment KPIs in 2 rows, insurance vs patient donut, payer bar, service-line bar, procedure donut, and MoM trend.',
        icon: '💳', category: 'Finance', tags: ['Payments','Donut','MoM'],
        gridH: 122,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'Charge',       x:0,  y:12,  w:40, h:16 },
            { role:'kpi',   label:'Payment',      x:40, y:12,  w:40, h:16 },
            { role:'kpi',   label:'Total AR',     x:80, y:12,  w:40, h:16 },
            { role:'kpi',   label:'Collection%',  x:0,  y:28, w:40, h:16 },
            { role:'kpi',   label:'Adj %',        x:40, y:28, w:40, h:16 },
            { role:'kpi',   label:'Days Collect', x:80, y:28, w:40, h:16 },
            { role:'donut', label:'Ins vs Pat',   x:0,  y:44, w:24, h:38 },
            { role:'bar',   label:'By Payer',     x:24, y:44, w:48, h:38 },
            { role:'bar',   label:'By Svc Line',  x:72, y:44, w:48, h:38 },
            { role:'donut', label:'By Procedure', x:0,  y:82, w:28, h:40 },
            { role:'bar',   label:'MoM Trend',    x:28, y:82, w:92, h:40 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'Total Charge',           layout:{x:0,  y:12,  w:40, h:16} },
            { type:'kpi',   title:'Total Payment',          layout:{x:40, y:12,  w:40, h:16} },
            { type:'kpi',   title:'Total AR',               layout:{x:80, y:12,  w:40, h:16} },
            { type:'kpi',   title:'Collection Rate %',      layout:{x:0,  y:28, w:40, h:16} },
            { type:'kpi',   title:'Adjustment Percentage',  layout:{x:40, y:28, w:40, h:16} },
            { type:'kpi',   title:'Days to Collect',        layout:{x:80, y:28, w:40, h:16} },
            { type:'donut', title:'Insurance vs Patient',   layout:{x:0,  y:44, w:24, h:38} },
            { type:'bar',   title:'Payments by Payer',      layout:{x:24, y:44, w:48, h:38} },
            { type:'bar',   title:'Payment by Service Line',layout:{x:72, y:44, w:48, h:38} },
            { type:'donut', title:'Pay by Procedure Group', layout:{x:0,  y:82, w:28, h:40} },
            { type:'bar',   title:'Month over Month Trend', layout:{x:28, y:82, w:92, h:40} },
        ],
    },

    // ── 7 ─ Operations Monitor ─────────────────────────────────────────────────
    {
        id: 'operations-monitor',
        name: 'Operations Monitor',
        description: '4 SLA/uptime KPIs, 3 gauge meters, combined trend line, and a live incident log table.',
        icon: '⚙️', category: 'Operations', tags: ['Gauge','Line','Table'],
        gridH: 94,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',   label:'SLA %',       x:0,  y:12,  w:30, h:18 },
            { role:'kpi',   label:'Throughput',  x:30, y:12,  w:30, h:18 },
            { role:'kpi',   label:'Error Rate',  x:60, y:12,  w:30, h:18 },
            { role:'kpi',   label:'Uptime',      x:90, y:12,  w:30, h:18 },
            { role:'gauge', label:'CPU',         x:0,  y:30, w:40, h:28 },
            { role:'gauge', label:'Memory',      x:40, y:30, w:40, h:28 },
            { role:'gauge', label:'Disk I/O',    x:80, y:30, w:40, h:28 },
            { role:'line',  label:'Sys Trend',   x:0,  y:58, w:80, h:36 },
            { role:'table', label:'Incidents',   x:80, y:58, w:40, h:36 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',   title:'SLA Compliance %',  layout:{x:0,  y:12,  w:30, h:18} },
            { type:'kpi',   title:'Throughput',        layout:{x:30, y:12,  w:30, h:18} },
            { type:'kpi',   title:'Error Rate',        layout:{x:60, y:12,  w:30, h:18} },
            { type:'kpi',   title:'System Uptime',     layout:{x:90, y:12,  w:30, h:18} },
            { type:'gauge', title:'CPU Utilization',   layout:{x:0,  y:30, w:40, h:28} },
            { type:'gauge', title:'Memory Usage',      layout:{x:40, y:30, w:40, h:28} },
            { type:'gauge', title:'Disk I/O',          layout:{x:80, y:30, w:40, h:28} },
            { type:'line',  title:'System Trend',      layout:{x:0,  y:58, w:80, h:36} },
            { type:'table', title:'Incident Log',      layout:{x:80, y:58, w:40, h:36} },
        ],
    },

    // ── 8 ─ Marketing Campaign ─────────────────────────────────────────────────
    {
        id: 'marketing-campaign',
        name: 'Marketing Campaign',
        description: '4 campaign KPIs, channel spend bar, conversion donut, audience scatter, and a full-width engagement trend.',
        icon: '🎯', category: 'Marketing', tags: ['Campaign','Scatter','Donut'],
        gridH: 110,
        preview: [
            { role:'text', label:'Dashboard Header', x:0, y:0, w:120, h:12 },
            { role:'kpi',     label:'Impressions',  x:0,  y:12,  w:30, h:18 },
            { role:'kpi',     label:'Clicks',       x:30, y:12,  w:30, h:18 },
            { role:'kpi',     label:'Conversions',  x:60, y:12,  w:30, h:18 },
            { role:'kpi',     label:'ROAS',         x:90, y:12,  w:30, h:18 },
            { role:'bar',     label:'By Channel',   x:0,  y:30, w:45, h:48 },
            { role:'donut',   label:'Conv Mix',     x:45, y:30, w:35, h:48 },
            { role:'scatter', label:'Audience',     x:80, y:30, w:40, h:48 },
            { role:'line',    label:'Engagement',   x:0,  y:78, w:120,h:32 },
        ],
        widgets: [
            { type:'pageHeader', title:'Dashboard Header', layout:{x:0, y:0, w:120, h:12} },
            { type:'kpi',     title:'Total Impressions',  layout:{x:0,  y:12,  w:30, h:18} },
            { type:'kpi',     title:'Total Clicks',       layout:{x:30, y:12,  w:30, h:18} },
            { type:'kpi',     title:'Conversions',        layout:{x:60, y:12,  w:30, h:18} },
            { type:'kpi',     title:'Return on Ad Spend', layout:{x:90, y:12,  w:30, h:18} },
            { type:'bar',     title:'Spend by Channel',   layout:{x:0,  y:30, w:45, h:48} },
            { type:'donut',   title:'Conversion Mix',     layout:{x:45, y:30, w:35, h:48} },
            { type:'scatter', title:'Audience Segments',  layout:{x:80, y:30, w:40, h:48} },
            { type:'line',    title:'Engagement Over Time',layout:{x:0, y:78, w:120,h:32} },
        ],
    },

];

// ── Category chip colors ──────────────────────────────────────────────────────
export const CATEGORY_STYLE: Record<string, string> = {
    Leadership:  'bg-purple-50 text-purple-700 border-purple-100 dark:bg-purple-900/20 dark:text-purple-300',
    Sales:       'bg-green-50 text-green-700 border-green-100 dark:bg-green-900/20 dark:text-green-300',
    Healthcare:  'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/20 dark:text-rose-300',
    Finance:     'bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-300',
    Operations:  'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-300',
    Marketing:   'bg-orange-50 text-orange-700 border-orange-100 dark:bg-orange-900/20 dark:text-orange-300',
};
